import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./language-switcher";

interface AuthLayoutProps {
  children: React.ReactNode;
}

export default function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel — Form (scrollable) */}
      <div className="flex-1 lg:w-1/2 bg-white flex flex-col">
        <div className="flex flex-col min-h-screen items-center justify-center p-6 lg:p-12">
          {/* Mobile / tablet header (hidden on desktop where the right panel shows brand) */}
          <div className="lg:hidden flex items-center justify-between mb-8 w-full max-w-md">
            <BrandLogo />
            <LanguageSwitcher />
          </div>

          {/* Form area */}
          <div className="w-full max-w-md">{children}</div>
        </div>
      </div>

      {/* Right Panel — Branding (sticky, desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-neutral-900 flex-col justify-between p-10 h-screen sticky top-0">
        {/* Top: Logo + Language */}
        <div className="flex items-start justify-between">
          <BrandLogo white />
          <LanguageSwitcher />
        </div>

        {/* Center: Tagline + Features */}
        <div className="space-y-8">
          <div>
            <h2 className="font-heading text-3xl font-bold text-white leading-tight">
              {t("auth.brand.tagline")}
            </h2>
            <p className="mt-3 text-neutral-400 text-base leading-relaxed">
              {t("auth.brand.description")}
            </p>
          </div>

          <ul className="space-y-4">
            {(
              [
                "auth.brand.features.bookings",
                "auth.brand.features.courts",
                "auth.brand.features.stats",
              ] as const
            ).map((key) => (
              <li key={key} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary-500 shrink-0" />
                <span className="text-neutral-300 text-sm">{t(key)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom: copyright */}
        <p className="text-neutral-600 text-xs">
          © {new Date().getFullYear()} SportScanner
        </p>
      </div>
    </div>
  );
}

function BrandLogo({ white = false }: { white?: boolean }) {
  return (
    <div className="flex items-center gap-1 select-none">
      <span
        className={`font-heading text-xl font-bold ${white ? "text-primary-400" : "text-primary-600"}`}
      >
        Sport
      </span>
      <span
        className={`font-heading text-xl font-bold ${white ? "text-white" : "text-neutral-800"}`}
      >
        Scanner
      </span>
    </div>
  );
}
